import React from "react";
import { useParams } from "react-router-dom";
import { Box, Grid2 as Grid, Typography } from "@mui/material";
import ViewPage from "../../components/view/ViewPage";
import ViewFieldCard from "../../components/view/fields/ViewFieldCard";
import ViewTextField from "../../components/view/fields/ViewTextField";
import ViewLinksField from "../../components/view/fields/ViewLinksField";
import ViewRichTextField from "../../components/view/fields/ViewRichTextField";
import ViewMultiSelectionRow from "../../components/view/fields/ViewMultiSelectionRow";
import { useEntityHandlerContext } from "../../controllers/entityController";
import usePlaceController from "../../controllers/placeController";
import { Place } from "../../types/place";
import PlacePersonsViewSection from "./sections/view/PlacePersonsViewSection";

const displayText = (value: unknown): string =>
  value !== undefined && value !== null && value !== "" ? String(value) : "N/A";

const RegionValue: React.FC<{ label: string; value?: string | null }> = ({
  label,
  value,
}) => (
  <Grid size={{ xs: 12, sm: 6 }}>
    <Box sx={{ mb: 2 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>
      <Typography variant="body1">{displayText(value)}</Typography>
    </Box>
  </Grid>
);

const PlaceRegionContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Place>();
  const place = methods.getValues();

  return (
    <>
      <RegionValue label="Region" value={place?.region?.parent} />
      <RegionValue label="Sub-region" value={place?.region?.child} />
    </>
  );
};

const PlaceDesignationsContent: React.FC = () => {
  const { methods } = useEntityHandlerContext<Place>();
  const place = methods.getValues();

  return <ViewMultiSelectionRow values={place?.other_designations ?? []} />;
};

const ViewPlacePage: React.FC = () => {
  const { _id } = useParams<{ _id: string }>();

  if (!_id) {
    throw new Error("Place ID is required for viewing.");
  }

  const controller = usePlaceController(_id);

  return (
    <ViewPage<Place>
      controller={controller}
      sections={(place) => [<PlacePersonsViewSection place={place} />]}
    >
      <ViewFieldCard title="Basic Information">
        <ViewTextField name="published_id" label="Intellexus ID" />
        <ViewTextField name="name" label="Name" />
        <ViewTextField name="cultural_sphere" label="Cultural Sphere" />
        <ViewTextField name="type" label="Type" />
      </ViewFieldCard>

      <ViewFieldCard title="Designations">
        <PlaceDesignationsContent />
      </ViewFieldCard>

      <ViewFieldCard title="Region">
        <PlaceRegionContent />
      </ViewFieldCard>

      <ViewFieldCard title="External Links">
        <ViewLinksField name="external_links" label="External Links" />
      </ViewFieldCard>

      <ViewFieldCard title="Notes">
        <ViewRichTextField name="notes" />
      </ViewFieldCard>
    </ViewPage>
  );
};

export default ViewPlacePage;
